// Wrapper de fetch para la API. Adjunta automáticamente el JWT desde localStorage,
// parsea JSON, y normaliza errores como `ApiError` con `status` y `code`.

const TOKEN_KEY = 'bini-trading:token';

export class ApiError extends Error {
  status: number;
  code?: string;
  fields?: Record<string, string[]>;

  constructor(message: string, opts: { status: number; code?: string; fields?: Record<string, string[]> }) {
    super(message);
    this.status = opts.status;
    this.code = opts.code;
    this.fields = opts.fields;
  }
}

export const tokenStorage = {
  get: (): string | null => localStorage.getItem(TOKEN_KEY),
  set: (token: string): void => localStorage.setItem(TOKEN_KEY, token),
  clear: (): void => localStorage.removeItem(TOKEN_KEY),
};

interface ApiOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
}

export async function api<T = unknown>(path: string, opts: ApiOptions = {}): Promise<T> {
  const headers = new Headers(opts.headers);
  // Solo seteamos Content-Type si hay body — Fastify rechaza con 400 si llega
  // application/json + body vacío (caso típico de POST sin payload).
  if (opts.body !== undefined) {
    headers.set('Content-Type', 'application/json');
  }
  const token = tokenStorage.get();
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const url = path.startsWith('/api') ? path : `/api${path.startsWith('/') ? '' : '/'}${path}`;

  const res = await fetch(url, {
    ...opts,
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });

  const text = await res.text();
  const data = text ? (JSON.parse(text) as unknown) : null;

  if (!res.ok) {
    const err = (data ?? {}) as { error?: string; code?: string; fields?: Record<string, string[]> };
    throw new ApiError(err.error || `HTTP ${res.status}`, {
      status: res.status,
      code: err.code,
      fields: err.fields,
    });
  }

  return data as T;
}
